/**
 * Phase 4.12 (M3) — Admin Spam-Detection Page.
 *
 * Listet aktive Inserate mit Spam-/Fake-Verdacht (heuristischer Score >= 30).
 * Klick auf einen Eintrag öffnet das Inserat in neuem Tab zur manuellen Prüfung.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { AlertTriangle, Loader2, ExternalLink, Sliders } from "lucide-react";

const LEVEL_COLORS = {
    high: "bg-red-100 text-red-800 border-red-200",
    medium: "bg-amber-100 text-amber-800 border-amber-200",
    low: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function AdminSpamFlagsPage() {
    const { user } = useAuth();
    const [data, setData] = useState({ flagged: [], total_scanned: 0 });
    const [loading, setLoading] = useState(true);
    const [minScore, setMinScore] = useState(30);

    useEffect(() => {
        if (user?.role !== "admin") return;
        let alive = true;
        setLoading(true);
        api.get(`/admin/compliance/spam-flags?limit=50&min_score=${minScore}`)
            .then(({ data: d }) => { if (alive) setData(d); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [user, minScore]);

    if (user?.role !== "admin") {
        return <div className="p-8 text-center text-gray-600">Nur für Admins.</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 py-10" data-testid="admin-spam-page">
            <div className="container mx-auto px-4 max-w-5xl">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
                            <AlertTriangle className="w-6 h-6 text-red-600" /> Spam-/Fake-Verdacht
                        </h1>
                        <p className="text-gray-600 text-sm mt-1">
                            {loading ? "Wird gescannt…" : `${data.flagged.length} verdächtige Inserate (von ${data.total_scanned} aktiven)`}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-sm px-3 py-2">
                        <Sliders className="w-4 h-4 text-gray-500" />
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Schwelle</label>
                        <select
                            value={minScore}
                            onChange={(e) => setMinScore(Number(e.target.value))}
                            className="bg-transparent text-sm font-mono-num"
                            data-testid="spam-threshold"
                        >
                            {[20, 30, 50, 70].map((v) => <option key={v} value={v}>≥ {v}</option>)}
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : data.flagged.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-600" data-testid="spam-empty">
                        Keine verdächtigen Inserate ab Score {minScore}. ✓
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="spam-flagged-list">
                        {data.flagged.map((it) => (
                            <div key={it.id} className="bg-white border-2 border-gray-200 rounded-sm p-4" data-testid={`spam-flag-${it.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${LEVEL_COLORS[it.level]}`}>
                                                {it.level} · Score {it.spam_score}
                                            </span>
                                            <span className="font-bold text-sm truncate">{it.title || "(ohne Titel)"}</span>
                                        </div>
                                        <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                            <span>{it.brand}</span>
                                            <span className="font-mono-num">{it.price ? `${it.price.toLocaleString("de-DE")} €` : "—"}</span>
                                            <span>{it.owner_email || "(Owner ohne E-Mail)"}</span>
                                        </div>
                                    </div>
                                    <Button asChild variant="outline" size="sm" className="rounded-sm">
                                        <Link to={`/listing/${it.id}`} target="_blank" data-testid={`spam-open-${it.id}`}>
                                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Inserat öffnen
                                        </Link>
                                    </Button>
                                </div>
                                <ul className="mt-3 space-y-1">
                                    {it.reasons.map((r, i) => (
                                        <li key={i} className="text-[11px] text-gray-700 flex items-center gap-2">
                                            <span className="inline-block min-w-[34px] text-right font-mono-num font-bold text-red-700">+{r.points}</span>
                                            <span>{r.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
